import { useEffect, useRef, useState } from 'react';
import { OptionRow } from '../components/Controls.tsx';
import { api, errorMessage } from '../lib/api.ts';
import { codexEffortLabel, type CodexModel } from '../../shared/codex-models.ts';

export function CodexModelFields({
  model,
  effort,
  onModel,
  onEffort,
  onLoaded,
  scope,
  defaultModel = 'gpt-6.1-sol',
  defaultEffort = '',
}: {
  scope: 'build' | 'chat';
  defaultModel?: string;
  defaultEffort?: string;
  model: string;
  effort: string;
  onModel: (model: string) => void;
  onEffort: (effort: string) => void;
  onLoaded: () => void;
}) {
  const [models, setModels] = useState<CodexModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const loaded = useRef(onLoaded);
  loaded.current = onLoaded;
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void api
      .get<{ models: CodexModel[] }>('/api/settings/codex/models')
      .then((result) => {
        if (cancelled) return;
        setModels(result.models);
        loaded.current();
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const selected = models.find((entry) => entry.model === (model || defaultModel));
  const efforts = selected?.supportedReasoningEfforts || [];
  const inheritedEffort = efforts.some((entry) => entry.reasoningEffort === defaultEffort)
    ? defaultEffort
    : selected?.defaultReasoningEffort;
  function chooseModel(next: string) {
    onModel(next);
    const entry = models.find((item) => item.model === (next || defaultModel));
    if (effort && !entry?.supportedReasoningEfforts.some((item) => item.reasoningEffort === effort)) onEffort('');
  }
  return (
    <>
      <OptionRow
        label={scope === 'chat' ? 'Chat model' : 'Build model'}
        hint={selected?.description || `Choose the GPT model for ${scope === 'chat' ? 'chat edits' : 'deck builds'}.`}
        note={
          loading ? (
            <p className="small muted" role="status">
              Loading Codex models…
            </p>
          ) : error ? (
            <div className="cred-stack">
              <p className="small error" role="alert">
                Cannot load models: {error}
              </p>
              <button className="btn btn--sm btn--ghost" onClick={() => setAttempt((value) => value + 1)}>
                Retry model list
              </button>
            </div>
          ) : undefined
        }
      >
        <select
          value={model}
          onChange={(e) => chooseModel(e.target.value)}
          disabled={loading || !models.length}
          aria-label={`Codex ${scope} model`}
        >
          <option value="">
            {scope === 'chat'
              ? `Same as builds · ${models.find((entry) => entry.model === defaultModel)?.displayName || defaultModel}`
              : 'Default · GPT-6.1 Sol'}
          </option>
          {model && !models.some((entry) => entry.model === model) && <option value={model}>{model} · saved</option>}
          {models.map((entry) => (
            <option key={entry.model} value={entry.model}>
              {entry.displayName}
            </option>
          ))}
        </select>
      </OptionRow>
      <OptionRow
        label="Thinking effort"
        hint={
          efforts.find((entry) => entry.reasoningEffort === effort)?.description ||
          (scope === 'chat'
            ? 'How hard the model thinks before it answers. The chat pill can change this for one session without changing these defaults.'
            : 'How hard the model thinks while it builds the deck. Its own dial, separate from chat.')
        }
      >
        <select
          value={effort}
          onChange={(e) => onEffort(e.target.value)}
          disabled={loading || !selected || !efforts.length}
          aria-label={`Codex ${scope} reasoning effort`}
        >
          <option value="">
            {scope === 'chat' ? 'Same as builds when supported' : 'Default'}
            {inheritedEffort ? ` · ${codexEffortLabel(inheritedEffort)}` : ''}
          </option>
          {effort && !efforts.some((entry) => entry.reasoningEffort === effort) && (
            <option value={effort} disabled>
              {codexEffortLabel(effort)} · saved, unavailable
            </option>
          )}
          {efforts.map((entry) => (
            <option key={entry.reasoningEffort} value={entry.reasoningEffort}>
              {codexEffortLabel(entry.reasoningEffort)}
            </option>
          ))}
        </select>
      </OptionRow>
    </>
  );
}
